'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { type ComponentProps, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import type { Input } from '../_base/Input';
import { cn } from '../../utils/cn';
import { Button } from '../_base/Button';
import { TextIconSwapAnimation } from '../feedback/TextIconSwapAnimation';
import { InputWithExtraContent } from './InputWithExtraContent';
const BUTTON_PADDING = 20;
const INLINE_PADDING = 12;
export interface InputWithActionProps extends Omit<ComponentProps<typeof Input>, 'children'> {
  onActionButtonClick: () => void;
  actionButtonLabel: string;
  /**
   * Hover/focus tooltip for the action button (`button` variant only). Requires a
   * `TooltipProvider` ancestor. Also becomes the button's `aria-label` when
   * `actionButtonAriaLabel` is not set, so it supplies the accessible name when
   * `actionButtonLabel` is empty (icon-only action).
   */
  actionButtonTooltip?: string;
  /**
   * Explicit `aria-label` for the action. Takes precedence over the `actionButtonTooltip`
   * backfill, so the accessible name survives when the tooltip is conditionally suppressed.
   * Applies to both action variants.
   */
  actionButtonAriaLabel?: string;
  actionButtonTestId?: string;
  isActionButtonDisabled?: boolean;
  isActionButtonHidden?: boolean;
  icon?: ReactNode;
  actionVariant?: 'button' | 'inline';
}
export function InputWithAction(t0) {
  const $ = _c(42);
  let actionButtonAriaLabel;
  let actionButtonLabel;
  let actionButtonTooltip;
  let icon;
  let inputProps;
  let onActionButtonClick;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      onActionButtonClick,
      actionButtonLabel,
      actionButtonTooltip,
      actionButtonAriaLabel,
      actionButtonTestId: t1,
      isActionButtonDisabled: t2,
      isActionButtonHidden: t3,
      icon,
      actionVariant: t4,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = actionButtonAriaLabel;
    $[2] = actionButtonLabel;
    $[3] = actionButtonTooltip;
    $[4] = icon;
    $[5] = inputProps;
    $[6] = onActionButtonClick;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    actionButtonAriaLabel = $[1];
    actionButtonLabel = $[2];
    actionButtonTooltip = $[3];
    icon = $[4];
    inputProps = $[5];
    onActionButtonClick = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  const actionButtonTestId = t1 === undefined ? "input-action-button" : t1;
  const isActionButtonDisabled = t2 === undefined ? false : t2;
  const isActionButtonHidden = t3 === undefined ? false : t3;
  const actionVariant = t4 === undefined ? "button" : t4;
  const textRef = useRef(null);
  const [width, setWidth] = useState(0);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setWidth(textRef.current?.offsetWidth ?? 0);
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== actionButtonLabel) {
    t6 = [actionButtonLabel];
    $[12] = actionButtonLabel;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useLayoutEffect(t5, t6);
  if (isActionButtonHidden) {
    let t7;
    if ($[14] !== inputProps) {
      t7 = <InputWithExtraContent extraContent={null} {...inputProps} />;
      $[14] = inputProps;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  const isInline = actionVariant === "inline";
  const t7 = width || "auto";
  let t8;
  if ($[16] !== t7) {
    t8 = {
      width: t7
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.15,
      type: "spring",
      bounce: 0
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== actionButtonLabel || $[20] !== icon) {
    t10 = <div ref={textRef} className="flex items-center gap-1">{icon}{actionButtonLabel}</div>;
    $[19] = actionButtonLabel;
    $[20] = icon;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== actionButtonLabel || $[23] !== t10) {
    t11 = <TextIconSwapAnimation animationKey={actionButtonLabel} duration={0.1}>{t10}</TextIconSwapAnimation>;
    $[22] = actionButtonLabel;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t8) {
    t12 = <motion.div animate={t8} transition={t9}>{t11}</motion.div>;
    $[25] = t11;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const animatedLabel = t12;
  let t13;
  if ($[28] !== actionButtonAriaLabel || $[29] !== actionButtonLabel || $[30] !== actionButtonTestId || $[31] !== actionButtonTooltip || $[32] !== animatedLabel || $[33] !== icon || $[34] !== isActionButtonDisabled || $[35] !== isInline || $[36] !== onActionButtonClick) {
    t13 = isInline ? <span role="button" tabIndex={0} data-testid={actionButtonTestId} data-input-action={true} className={cn("text-info-foreground relative overflow-hidden text-xs font-medium select-none", isActionButtonDisabled ? "cursor-default opacity-50" : "cursor-pointer")} onMouseDown={_temp} onClick={isActionButtonDisabled ? undefined : onActionButtonClick} onKeyDown={e_0 => {
      if (e_0.key === "Enter" || e_0.key === " ") {
        e_0.preventDefault();
        if (!isActionButtonDisabled) {
          onActionButtonClick();
        }
      }
    }} aria-disabled={isActionButtonDisabled || undefined} aria-label={actionButtonAriaLabel}>{animatedLabel}</span> : <Button type="button" variant="secondary" size={actionButtonLabel ? "sm" : "iconSm"} className="relative" data-testid={actionButtonTestId} data-input-action={true} tooltip={actionButtonTooltip} aria-label={actionButtonAriaLabel} onMouseDown={_temp2} onClick={onActionButtonClick} disabled={isActionButtonDisabled}>{actionButtonLabel ? animatedLabel : icon}</Button>;
    $[28] = actionButtonAriaLabel;
    $[29] = actionButtonLabel;
    $[30] = actionButtonTestId;
    $[31] = actionButtonTooltip;
    $[32] = animatedLabel;
    $[33] = icon;
    $[34] = isActionButtonDisabled;
    $[35] = isInline;
    $[36] = onActionButtonClick;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const actionContent = t13;
  const t14 = isInline ? INLINE_PADDING : BUTTON_PADDING;
  let t15;
  if ($[38] !== actionContent || $[39] !== inputProps || $[40] !== t14) {
    t15 = <InputWithExtraContent extraContentPadding={t14} extraContent={actionContent} {...inputProps} />;
    $[38] = actionContent;
    $[39] = inputProps;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
}
function _temp2(e_1) {
  e_1.preventDefault();
}
function _temp(e) {
  e.preventDefault();
}